import type {
    OrderReportResponse,
    ReportCostsResource,
    ReportFindingResource,
    ReportInvoiceResource,
    ReportNhgResource,
    ReportResource,
    ReportSectionResource,
    ReportShareResource,
    ReportStatisticsResource,
    PdfBacklogResource,
} from './types';

/**
 * Het rapport is een **bijproduct** van de opname, geen fase. De eigenaar
 * leest na, stelt hoogstens een tekst bij en keurt goed; goedkeuren is
 * versturen (principe 5).
 *
 * De schermen kennen alleen de vorm in dit bestand. De `to*`-functies zijn de
 * enige plek waar de lijnvorm uit `types.ts` wordt vertaald.
 */

/** De drie woorden van de app: `status` in de API. */
export const REPORT_STATUSES = ['concept', 'goedgekeurd', 'verstuurd'] as const;

export type ReportStatus = (typeof REPORT_STATUSES)[number];

/** De hele levenscyclus: `state` in de API. */
export const REPORT_STATES = [
    'generating',
    'draft',
    'approved',
    'sent',
    'failed',
    'superseded',
] as const;

export type ReportState = (typeof REPORT_STATES)[number];

export const REPORT_COST_KEYS = ['direct', 'termijn', 'onderhoud'] as const;

export type ReportCostKey = (typeof REPORT_COST_KEYS)[number];

/** Bedragen in euro's. */
export type ReportCosts = Record<ReportCostKey, number>;

export const REPORT_SECTION_KINDS = [
    'summary',
    'guide',
    'costs',
    'part',
    'nhg',
    'conditions',
] as const;

export type ReportSectionKind = (typeof REPORT_SECTION_KINDS)[number];

export const REPORT_FINDING_SEVERITIES = ['good', 'attention', 'defect', 'none'] as const;

export type ReportFindingSeverity = (typeof REPORT_FINDING_SEVERITIES)[number];

export interface ReportAddress {
    street: string;
    houseNumber: string;
    postalCode: string;
    city: string;
}

export interface ReportSection {
    uuid: string;
    key: string;
    kind: ReportSectionKind;
    /** "Samenvatting", "3 Buitenzijde". */
    label: string;
    /** "2" of "4–7"; leeg zolang de PDF nog niet gemaakt is. */
    pages: string;
    /**
     * Waar dit hoofdstuk in de boom van de opname staat (`13.2`), zodat je het
     * daar kunt openen. `null` voor Samenvatting, Kosten, NHG en Voorwaarden:
     * die horen bij geen onderdeel.
     */
    sectionPath: string | null;
    /** De aangepaste tekst, of de gegenereerde zolang niemand iets schreef. */
    body: string | null;
    bodyIsGenerated: boolean;
    bodyUpdatedAt: string | null;
    /**
     * Staat achteraan, in de bijlage "Toelichting en voorwaarden": de onderdelen
     * die alleen uitleggen, en de voorwaarden. De lijst staat in de volgorde van
     * de PDF en de klantpagina.
     */
    appendix: boolean;
}

/** De NHG-keuze van de inspecteur voor een bevinding. */
export const REPORT_NHG_CLASSIFICATIONS = ['direct', 'vijfjaar', 'niet'] as const;

export type ReportNhgClassification = (typeof REPORT_NHG_CLASSIFICATIONS)[number];

/** Waarom een bevinding met kosten niet in de NHG-bijlage staat. */
export type ReportNhgOutsideReason = 'no_code' | 'excluded';

/** Waar een bevinding in de NHG-bijlage landt, en met welke bedragen. */
export interface ReportFindingNhg {
    /** De vaste regel, of `null` buiten de tabellen. */
    row: string | null;
    element: string | null;
    direct: number;
    termijn: number;
    /** Verbeterkosten: de losse regel Verbetering. */
    onderhoud: number;
    outsideReason: ReportNhgOutsideReason | null;
}

/** Wie het geld van een bevinding na het indienen aanpaste, en of de inspecteur akkoord is. */
export interface ReportFindingAdjustment {
    byName: string | null;
    at: string | null;
    reason: string | null;
    byInspector: boolean;
    awaitsInspector: boolean;
    confirmedByName: string | null;
    confirmedAt: string | null;
}

/** Bedragen die met de hand zijn gezet; `null` waar de berekening telt. */
export type ReportOwnAmounts = Record<ReportCostKey, number | null>;

export interface ReportFinding {
    key: string;
    /** Bouwdeel: "Kozijnen". */
    part: string;
    /** De kop waaronder de bevinding staat: "35 Kozijnen". */
    section: string;
    /** Het nummer van die kop: "35". */
    sectionNumber: string;
    /**
     * Het boompad van de knoop in de opname waar de bevinding vandaan komt
     * (`13.2.1`). `null` bij een handmatige bevinding zonder plek in de boom.
     */
    sectionPath: string | null;
    finding: string;
    explanation: string;
    /** Waar het gebrek zit, in de woorden van de inspecteur; `null` als er niets staat. */
    location: string | null;
    /** Het label uit de woordenlijst: "Direct", "Op termijn", "Verbetering". */
    urgency: string;
    urgencyKey: ReportCostKey | null;
    severity: ReportFindingSeverity;
    cost: number;
    /** Wat telt: de berekening, met de eigen bedragen erin. */
    costs: ReportCosts;
    /** Het NHG-onderdeel waaronder de bevinding valt. */
    nhgCode: string | null;
    /** Een met de hand gekozen NHG-onderdeel; `null` als `nhgCode` uit de vragenlijst komt. */
    ownNhgCode: string | null;
    nhgClassification: ReportNhgClassification | null;
    nhg: ReportFindingNhg | null;
    /** Een bevinding die de inspecteur zelf toevoegde: haar bedragen zijn ingetypt. */
    manual: boolean;
    /** Het ingevulde aantal bij een vraag naar een hoeveelheid; `null` bij een vast bedrag. */
    quantity: number | null;
    /** Het bedrag per eenheid (of het vaste bedrag) uit de vragenlijst. */
    unitCosts: ReportCosts | null;
    /** Wat de vragenlijst rekent: eenheidsprijs maal hoeveelheid. */
    calculatedCosts: ReportCosts | null;
    /** Er staat met de hand een bedrag op. */
    costsAdjusted: boolean;
    ownAmounts: ReportOwnAmounts | null;
    adjustment: ReportFindingAdjustment | null;
}

export interface ReportNhgRow {
    code: string;
    element: string;
    locations: string[];
    /** "H" bij kosten, anders leeg. */
    action: string;
    direct: number;
    termijn: number;
    findingKeys: string[];
}

export interface ReportNhgTable {
    key: 'general' | 'storeys';
    label: string;
    rows: ReportNhgRow[];
    total: { direct: number; termijn: number };
}

/** De NHG-bijlage zoals in de PDF. */
export interface ReportNhg {
    enabled: boolean;
    hasFindings: boolean;
    tables: ReportNhgTable[];
    total: { direct: number; termijn: number };
    /** Verbeterkosten van bevindingen in de tabellen. */
    improvement: number;
    /** Wat het rapport telt en de bijlage niet. */
    outside: ReportCosts & { count: number };
}

export interface ReportPhoto {
    uuid: string;
    caption: string;
    /** Kortlevende link. */
    url: string;
}

export interface ReportRecipient {
    name: string;
    /** `null`: geen e-mailadres, dus deze ontvanger krijgt niets. */
    email: string | null;
    phone: string | null;
    /** "Opdrachtgever", "Makelaar", "Adviseur". */
    role: string;
    roleKey: string;
    sentAt: string | null;
}

export interface ReportCounts {
    findings: number;
    defects: number;
    attentionPoints: number;
    photos: number;
    parts: number;
    partsWithoutFindings: number;
    questionsVisible: number;
    questionsAnswered: number;
}

/** Wat deze persoon met dit rapport mag; de API beslist per rapport. */
export interface ReportAbilities {
    edit: boolean;
    approve: boolean;
    send: boolean;
    share: boolean;
    /** Het geld van een bevinding aanpassen, zolang het een concept is. */
    adjust: boolean;
    /** Akkoord geven op zo'n aanpassing: alleen de inspecteur van de opname. */
    confirm: boolean;
}

export interface Report {
    uuid: string;
    orderUuid: string;
    orderNumber: string;
    version: number;
    status: ReportStatus;
    state: ReportState;
    stateLabel: string;
    /** Een concept waarvan de teksten nog bij mogen. */
    editable: boolean;
    /**
     * De opname van de keuring staat weer open (heropend) of is nog niet ingediend.
     * Goedkeuren kan dan niet: het concept past misschien niet meer bij de opname.
     */
    surveyOpen: boolean;
    failureReason: string | null;
    normLabel: string;
    kindLabel: string;
    service: string;
    address: ReportAddress;
    requestedAt: string | null;
    generatedAt: string | null;
    generationDurationMs: number | null;
    minutesAfterInspection: number;
    clientName: string;
    inspectedOn: string | null;
    inspectorName: string;
    costs: ReportCosts;
    costGroups: Array<ReportCosts & { name: string }>;
    counts: ReportCounts;
    summary: string;
    sections: ReportSection[];
    /** De bevindingen **met kosten** (of met aangepaste kosten), meest urgent en duurst eerst. */
    findings: ReportFinding[];
    /** `null` voor een rapport van voor de bijlage in de API stond. */
    nhg: ReportNhg | null;
    /** Aangepaste bedragen waar de inspecteur nog niet mee akkoord is. */
    adjustmentsAwaitingInspector: number;
    photos: ReportPhoto[];
    /**
     * Foto's van de opname die niet (of nog niet) verwerkt zijn en dus niet in het
     * rapport staan: `failed` gaf de server op, `waiting` wacht nog op verwerking.
     */
    photosUnprocessed: { failed: number; waiting: number };
    recipients: ReportRecipient[];
    approvedAt: string | null;
    approvedByName: string | null;
    sentAt: string | null;
    /**
     * De versie die deze goedgekeurde versie voor de koper verving, achter dezelfde link;
     * `null` voor de eerste die uitging en voor een concept. Een correctie die zonder
     * bericht is vastgesteld heeft geen `sentAt`, maar staat wel bij de koper.
     */
    replacesVersion: number | null;
    pageCount: number | null;
    /**
     * De talen waarin de klant het rapport kan krijgen ("Taal voor de klant" in de
     * verstuurdialoog), Nederlands eerst. Alleen `['nl']` zolang de server niet
     * vertaalt: dan vraagt de dialoog niets.
     */
    clientLanguages: string[];
    can: ReportAbilities;
}

export function toReport(resource: ReportResource): Report {
    return {
        uuid: resource.uuid,
        orderUuid: resource.order_uuid,
        orderNumber: resource.order_number,
        version: resource.version,
        status: oneOf(REPORT_STATUSES, resource.status, 'concept'),
        state: oneOf(REPORT_STATES, resource.state, 'draft'),
        stateLabel: resource.state_label,
        editable: resource.editable,
        surveyOpen: resource.survey_open,
        failureReason: resource.failure_reason,
        normLabel: resource.norm_label,
        kindLabel: resource.kind_label,
        service: resource.service,
        address: {
            street: resource.address.street,
            houseNumber: resource.address.house_number,
            postalCode: resource.address.postal_code,
            city: resource.address.city,
        },
        requestedAt: resource.requested_at,
        generatedAt: resource.generated_at,
        generationDurationMs: resource.generation_duration_ms,
        minutesAfterInspection: resource.minutes_after_inspection,
        clientName: resource.client_name,
        inspectedOn: resource.inspected_on === '' ? null : resource.inspected_on,
        inspectorName: resource.inspector_name,
        costs: toCosts(resource.costs),
        costGroups: resource.cost_groups.map((group) => ({ name: group.name, ...toCosts(group) })),
        counts: {
            findings: resource.counts.findings,
            defects: resource.counts.defects,
            attentionPoints: resource.counts.attention_points,
            photos: resource.counts.photos,
            parts: resource.counts.parts,
            partsWithoutFindings: resource.counts.parts_without_findings,
            questionsVisible: resource.counts.questions_visible,
            questionsAnswered: resource.counts.questions_answered,
        },
        summary: resource.summary,
        sections: resource.sections.map(toSection),
        findings: resource.findings.map(toFinding),
        nhg: resource.nhg === null ? null : toNhg(resource.nhg),
        adjustmentsAwaitingInspector: resource.adjustments_awaiting_inspector,
        photos: resource.photos.map((photo) => ({
            uuid: photo.uuid,
            caption: photo.caption,
            url: photo.url,
        })),
        photosUnprocessed: {
            failed: resource.photos_unprocessed.failed,
            waiting: resource.photos_unprocessed.waiting,
        },
        recipients: resource.recipients.map((recipient) => ({
            name: recipient.name,
            email: recipient.email.trim() === '' ? null : recipient.email,
            phone: recipient.phone,
            role: recipient.role,
            roleKey: recipient.role_key,
            sentAt: recipient.sent_at,
        })),
        approvedAt: resource.approved_at,
        approvedByName: resource.approved_by_name,
        sentAt: resource.sent_at,
        replacesVersion: resource.replaces_version ?? null,
        pageCount: resource.page_count,
        clientLanguages: resource.client_languages.length > 0 ? resource.client_languages : ['nl'],
        can: { ...resource.can },
    };
}

function toSection(resource: ReportSectionResource): ReportSection {
    return {
        uuid: resource.uuid,
        key: resource.key,
        kind: oneOf(REPORT_SECTION_KINDS, resource.kind, 'part'),
        label: resource.label,
        pages: resource.pages,
        sectionPath: resource.section_path === '' ? null : resource.section_path,
        body: resource.body,
        bodyIsGenerated: resource.body_is_generated,
        bodyUpdatedAt: resource.body_updated_at,
        appendix: resource.appendix ?? false,
    };
}

function toFinding(resource: ReportFindingResource): ReportFinding {
    const classification = resource.nhg_classification;

    return {
        key: resource.key,
        part: resource.part,
        section: resource.section,
        sectionNumber: resource.section_number,
        sectionPath: resource.section_path === '' ? null : resource.section_path,
        finding: resource.finding,
        explanation: resource.explanation,
        location: resource.location,
        urgency: resource.urgency,
        urgencyKey:
            resource.urgency_key === null
                ? null
                : oneOf(REPORT_COST_KEYS, resource.urgency_key, 'termijn'),
        severity: oneOf(REPORT_FINDING_SEVERITIES, resource.severity, 'none'),
        cost: toAmount(resource.cost),
        costs: toCosts(resource.costs),
        nhgCode: resource.nhg_code,
        ownNhgCode: resource.own_nhg_code,
        nhgClassification:
            classification !== null &&
            (REPORT_NHG_CLASSIFICATIONS as readonly string[]).includes(classification)
                ? (classification as ReportNhgClassification)
                : null,
        nhg:
            resource.nhg === null
                ? null
                : {
                      row: resource.nhg.row,
                      element: resource.nhg.element,
                      direct: toAmount(resource.nhg.direct),
                      termijn: toAmount(resource.nhg.termijn),
                      onderhoud: toAmount(resource.nhg.onderhoud),
                      outsideReason:
                          resource.nhg.outside_reason === 'no_code' ||
                          resource.nhg.outside_reason === 'excluded'
                              ? resource.nhg.outside_reason
                              : null,
                  },
        manual: resource.source === 'manual',
        quantity: resource.quantity,
        unitCosts: resource.unit_costs === null ? null : toCosts(resource.unit_costs),
        calculatedCosts:
            resource.calculated_costs === null ? null : toCosts(resource.calculated_costs),
        costsAdjusted: resource.costs_adjusted,
        ownAmounts:
            resource.own_amounts === null
                ? null
                : {
                      direct: toOwnAmount(resource.own_amounts.direct),
                      termijn: toOwnAmount(resource.own_amounts.termijn),
                      onderhoud: toOwnAmount(resource.own_amounts.onderhoud),
                  },
        adjustment:
            resource.adjustment === null
                ? null
                : {
                      byName: resource.adjustment.by_name,
                      at: resource.adjustment.at,
                      reason: resource.adjustment.reason,
                      byInspector: resource.adjustment.by_inspector,
                      awaitsInspector: resource.adjustment.awaits_inspector,
                      confirmedByName: resource.adjustment.confirmed_by_name,
                      confirmedAt: resource.adjustment.confirmed_at,
                  },
    };
}

function toNhg(resource: ReportNhgResource): ReportNhg {
    return {
        enabled: resource.enabled,
        hasFindings: resource.has_findings,
        tables: resource.tables.map((table) => ({
            key: table.key === 'storeys' ? 'storeys' : 'general',
            label: table.label,
            rows: table.rows.map((row) => ({
                code: row.code,
                element: row.element,
                locations: row.locations,
                action: row.action,
                direct: toAmount(row.direct),
                termijn: toAmount(row.termijn),
                findingKeys: row.finding_keys,
            })),
            total: { direct: toAmount(table.total.direct), termijn: toAmount(table.total.termijn) },
        })),
        total: {
            direct: toAmount(resource.total.direct),
            termijn: toAmount(resource.total.termijn),
        },
        improvement: toAmount(resource.improvement),
        outside: {
            direct: toAmount(resource.outside.direct),
            termijn: toAmount(resource.outside.termijn),
            onderhoud: toAmount(resource.outside.onderhoud),
            count: resource.outside.count,
        },
    };
}

function toOwnAmount(value: string | null): number | null {
    return value === null ? null : toAmount(value);
}

function toCosts(resource: ReportCostsResource): ReportCosts {
    return {
        direct: toAmount(resource.direct),
        termijn: toAmount(resource.termijn),
        onderhoud: toAmount(resource.onderhoud),
    };
}

/** `"1450.00"` → `1450`. Een onleesbaar bedrag wordt 0, nooit `NaN` op het scherm. */
function toAmount(value: string): number {
    const amount = Number.parseFloat(value);

    return Number.isFinite(amount) ? amount : 0;
}

function oneOf<T extends string>(values: readonly T[], value: string, fallback: T): T {
    return (values as readonly string[]).includes(value) ? (value as T) : fallback;
}

/* -------------------------------------------------------------------------- */
/* Levenscyclus                                                                */
/* -------------------------------------------------------------------------- */

/** Staat nog in de wachtrij of wordt nu gemaakt. */
export function isGenerating(report: Report): boolean {
    return report.state === 'generating';
}

/** Goedgekeurd of verstuurd: er bestaat een publieke link. */
export function isReleased(report: Report): boolean {
    return report.status !== 'concept';
}

/** Teksten bijstellen: een concept, en deze persoon mag het. */
export function canEditTexts(report: Report): boolean {
    return report.editable && report.can.edit;
}

/** Goedkeuren (= versturen): alleen een klaar concept. */
export function canApprove(report: Report): boolean {
    return report.state === 'draft' && report.status === 'concept' && report.can.approve;
}

/**
 * Het geld van een bevinding aanpassen: een concept dat nog niet is
 * goedgekeurd, waarvan de opname is ingediend, en deze persoon mag het.
 */
export function canAdjustCosts(report: Report): boolean {
    return report.editable && !report.surveyOpen && report.can.adjust;
}

/**
 * De knop staat er wel, maar werkt nog niet: de inspecteur is nog niet akkoord
 * met een bedrag dat een ander aanpaste. De API zou goedkeuren weigeren
 * (`report-not-approvable`).
 */
export function approvalWaitsForInspector(report: Report): boolean {
    return canApprove(report) && report.adjustmentsAwaitingInspector > 0;
}

/** De keuzes voor het NHG-onderdeel: de vaste regels van de bijlage, in volgorde. */
export function nhgRowOptions(report: Report): Array<{ code: string; element: string }> {
    return (report.nhg?.tables ?? []).flatMap((table) =>
        table.rows.map((row) => ({ code: row.code, element: row.element })),
    );
}

/**
 * De bevindingen achter een NHG-regel, in de volgorde van de bijlage. Komen uit
 * `findingKeys`, zodat de uitsplitsing altijd optelt tot de regel.
 */
export function findingsOfNhgRow(report: Report, row: ReportNhgRow): ReportFinding[] {
    const byKey = new Map(report.findings.map((finding) => [finding.key, finding]));

    return row.findingKeys.flatMap((key) => {
        const finding = byKey.get(key);

        return finding ? [finding] : [];
    });
}

/**
 * De bevindingen met kosten die niet op een regel van de bijlage staan: zonder
 * NHG-onderdeel, niet opgenomen, alleen verbeterkosten, of aangepast naar nul.
 */
export function findingsBesideNhgRows(report: Report): {
    outside: ReportFinding[];
    improvementOnly: ReportFinding[];
    withoutAmount: ReportFinding[];
} {
    const listed = new Set(
        (report.nhg?.tables ?? []).flatMap((table) => table.rows.flatMap((row) => row.findingKeys)),
    );
    const outside: ReportFinding[] = [];
    const improvementOnly: ReportFinding[] = [];
    const withoutAmount: ReportFinding[] = [];

    for (const finding of report.findings) {
        if (listed.has(finding.key)) {
            continue;
        }

        if (finding.nhg?.outsideReason) {
            outside.push(finding);
        } else if ((finding.nhg?.onderhoud ?? 0) > 0) {
            improvementOnly.push(finding);
        } else {
            withoutAmount.push(finding);
        }
    }

    return { outside, improvementOnly, withoutAmount };
}

/**
 * De knop staat er wel, maar werkt nog niet: de opname staat weer open. De API
 * zou goedkeuren weigeren (`report-not-approvable`); dat hoort vóór de klik te
 * blijken, niet erna.
 */
export function approvalWaitsForSurvey(report: Report): boolean {
    return canApprove(report) && report.surveyOpen;
}

/** Wie het rapport krijgt: alleen ontvangers met een e-mailadres. */
export function deliverableRecipients(report: Report): ReportRecipient[] {
    return report.recipients.filter((recipient) => recipient.email !== null);
}

/** De gekozen sectie, of de eerste met tekst. */
export function sectionByUuid(report: Report, uuid: string | null): ReportSection | null {
    if (uuid !== null) {
        const chosen = report.sections.find((section) => section.uuid === uuid);

        if (chosen) {
            return chosen;
        }
    }

    return report.sections.find((section) => section.body !== null) ?? report.sections[0] ?? null;
}

/** "Kerkweg 41, Barendrecht" */
export function formatReportAddress(address: ReportAddress): string {
    const street = [address.street, address.houseNumber].filter(Boolean).join(' ');

    return [street, address.city].filter(Boolean).join(', ');
}

/** "Kerkweg 41, 2991 AB Barendrecht" */
export function formatReportFullAddress(address: ReportAddress): string {
    const street = [address.street, address.houseNumber].filter(Boolean).join(' ');
    const place = [address.postalCode, address.city].filter(Boolean).join(' ');

    return [street, place].filter(Boolean).join(', ');
}

/** Zoals de API het bestand noemt: `rapport-24-1183-v2.pdf`. */
export function reportPdfFilename(report: Pick<Report, 'orderNumber' | 'version'>): string {
    const slug = report.orderNumber
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    return `rapport-${slug === '' ? 'keuring' : slug}-v${report.version}.pdf`;
}

/* -------------------------------------------------------------------------- */
/* Goedkeuren en versturen                                                     */
/* -------------------------------------------------------------------------- */

export interface ReportDelivery {
    /** Ontvangers die het rapport kregen. */
    sent: number;
    failed: number;
    /** `email`, `sms`. */
    channels: string[];
}

/**
 * "Factuur meesturen" voor één bezorging: of de factuur mee moet, en het eigen bericht
 * van kantoor in die mail.
 */
export interface ReportInvoiceChoice {
    includeInvoice: boolean;
    /** Het eigen bericht; leeg mag. Gaat alleen mee met de factuur. */
    message: string;
}

/** De factuur die als pdf meeging naar de klant. */
export interface ReportEnclosedInvoice {
    uuid: string;
    number: string;
    total: string;
    dueAt: string;
    filename: string;
    /** Net uit de diensten van de keuring gemaakt. */
    drafted: boolean;
    /** Net genummerd en gedateerd: een concept werd definitief. */
    issued: boolean;
}

/**
 * Wat goedkeuren oplevert. De toegangscode staat alleen hier, één keer: hij
 * hoort in het geheugen van het scherm en nergens anders.
 */
export interface ReportApproval {
    report: Report;
    link: ReportDeliveredLink;
    delivery: ReportDelivery;
    /** De factuur die meeging; `null` als er geen meeging. */
    invoice: ReportEnclosedInvoice | null;
}

/** De link die met een bezorging meeging. */
export interface ReportDeliveredLink {
    shareUuid: string;
    url: string;
    validUntil: string;
    /** Alleen als deze link net gemaakt is en een code vraagt: eenmalig. */
    accessCode: string | null;
}

/**
 * Wat opnieuw versturen oplevert. Werkte er geen link meer, dan maakte de API
 * een nieuwe (`newLink`); vraagt die een code, dan staat die hier één keer.
 */
export interface ReportResend {
    report: Report;
    link: ReportDeliveredLink;
    newLink: boolean;
    delivery: ReportDelivery;
    invoice: ReportEnclosedInvoice | null;
}

export function toReportEnclosedInvoice(
    resource: ReportInvoiceResource | null,
): ReportEnclosedInvoice | null {
    return resource === null
        ? null
        : {
              uuid: resource.uuid,
              number: resource.number,
              total: resource.total,
              dueAt: resource.due_at,
              filename: resource.filename,
              drafted: resource.drafted,
              issued: resource.issued,
          };
}

/* -------------------------------------------------------------------------- */
/* Publieke links                                                              */
/* -------------------------------------------------------------------------- */

export const REPORT_SHARE_STATES = ['active', 'expired', 'revoked'] as const;

export type ReportShareState = (typeof REPORT_SHARE_STATES)[number];

export interface ReportShare {
    uuid: string;
    reportUuid: string;
    url: string | null;
    state: ReportShareState;
    validUntil: string;
    showCosts: boolean;
    allowForward: boolean;
    accessCodeRequired: boolean;
    openedCount: number;
    downloadedCount: number;
    forwardedCount: number;
    lastOpenedAt: string | null;
    revokedAt: string | null;
    createdAt: string | null;
}

/** Een link met, eenmalig, de code die bij deze wijziging hoort. */
export interface IssuedReportShare {
    share: ReportShare;
    accessCode: string | null;
}

export function toReportShare(resource: ReportShareResource): ReportShare {
    return {
        uuid: resource.uuid,
        reportUuid: resource.report_uuid,
        url: resource.url,
        state: oneOf(REPORT_SHARE_STATES, resource.state, 'active'),
        validUntil: resource.valid_until,
        showCosts: resource.show_costs,
        allowForward: resource.allow_forward,
        accessCodeRequired: resource.access_code_required,
        openedCount: resource.opened_count,
        downloadedCount: resource.downloaded_count,
        forwardedCount: resource.forwarded_count,
        lastOpenedAt: resource.last_opened_at,
        revokedAt: resource.revoked_at,
        createdAt: resource.created_at,
    };
}

/* -------------------------------------------------------------------------- */
/* De versie die de koper heeft                                                */
/* -------------------------------------------------------------------------- */

/** De nieuwste goedgekeurde versie: die opent de koper met zijn link. */
export interface ReleasedReport {
    reportUuid: string;
    version: number;
    /** De nieuwste link van die versie die nog werkt; `null` als er geen meer werkt. */
    link: ReportShare | null;
}

/**
 * Het rapport van een keuring: de nieuwste versie, en de versie die de koper
 * heeft. Na "Nieuwe versie maken" zijn dat er twee; anders wijzen ze naar
 * hetzelfde rapport, of is er (nog) niets verstuurd.
 */
export interface OrderReport {
    report: Report;
    released: ReleasedReport | null;
}

export function toOrderReport(response: OrderReportResponse): OrderReport {
    const { meta } = response;

    return {
        report: toReport(response.data),
        released:
            meta.released_report_uuid === null
                ? null
                : {
                      reportUuid: meta.released_report_uuid,
                      version: meta.released_version ?? response.data.version,
                      link: meta.released_link === null ? null : toReportShare(meta.released_link),
                  },
    };
}

/** Staat er naast de nieuwste versie nog een oudere die de koper heeft? */
export function hasEarlierReleasedVersion(orderReport: OrderReport): boolean {
    return (
        orderReport.released !== null && orderReport.released.reportUuid !== orderReport.report.uuid
    );
}

/**
 * De link waar de instellingen van de klantpagina over gaan: de nieuwste die
 * niet is ingetrokken, anders de nieuwste. De API levert ze nieuwste eerst.
 */
export function currentShare(shares: readonly ReportShare[]): ReportShare | null {
    return shares.find((share) => share.state !== 'revoked') ?? shares[0] ?? null;
}

/** Wat `PATCH /reports/{report}/findings/{finding}` accepteert: de hele nieuwe stand. */
export interface ReportFindingAdjustmentInput {
    /** Euro's; `null` is weer het bedrag uit de vragenlijst (bij een handmatige bevinding: € 0). */
    amounts: ReportOwnAmounts;
    nhgClassification: ReportNhgClassification | null;
    /** Een vaste regel van de bijlage, of `null` voor het onderdeel uit de vragenlijst. */
    nhgCode: string | null;
    reason: string;
}

/** Wat `PATCH /reports/{report}/shares/{share}` accepteert. */
export interface ReportShareChanges {
    validDays?: number;
    showCosts?: boolean;
    allowForward?: boolean;
    requireAccessCode?: true;
    removeAccessCode?: true;
    revoke?: true;
}

/* -------------------------------------------------------------------------- */
/* Linkstatistieken                                                            */
/* -------------------------------------------------------------------------- */

export const REPORT_LINK_STATES = ['none', 'active', 'expired', 'revoked'] as const;

export type ReportLinkState = (typeof REPORT_LINK_STATES)[number];

export interface ReportLinkEvent {
    type: string;
    /** "Geopend", "Vraag gesteld". */
    label: string;
    channel: string | null;
    /** Gemaskeerd: genoeg om te herkennen, niet om over te nemen. */
    recipient: string | null;
    /** De vraag van de koper, bij een gestelde vraag. */
    message: string | null;
    occurredAt: string;
}

/**
 * Tellingen over alle links van het rapport, met de instellingen van de
 * nieuwste link die niet is ingetrokken.
 */
export interface ReportLinkStatistics {
    sentAt: string | null;
    /** "e-mail en sms". */
    sentVia: string | null;
    openedCount: number;
    openedLastAt: string | null;
    downloadedCount: number;
    forwardedCount: number;
    questionsCount: number;
    validUntil: string | null;
    url: string | null;
    shortUrl: string | null;
    costsVisible: boolean;
    sharingEnabled: boolean;
    accessCodeRequired: boolean;
    linkState: ReportLinkState;
    linksCount: number;
    events: ReportLinkEvent[];
}

export function toReportLinkStatistics(resource: ReportStatisticsResource): ReportLinkStatistics {
    return {
        sentAt: resource.sent_at,
        sentVia: resource.sent_via,
        openedCount: resource.opened_count,
        openedLastAt: resource.opened_last_at,
        downloadedCount: resource.downloaded_count,
        forwardedCount: resource.forwarded_count,
        questionsCount: resource.questions_count,
        validUntil: resource.valid_until,
        url: resource.url === '' ? null : resource.url,
        shortUrl: resource.short_url,
        costsVisible: resource.costs_visible,
        sharingEnabled: resource.sharing_enabled,
        accessCodeRequired: resource.access_code_required,
        linkState: oneOf(REPORT_LINK_STATES, resource.link_state, 'none'),
        linksCount: resource.links_count,
        events: resource.events.map((event) => ({
            type: event.type,
            label: event.label,
            channel: event.channel,
            recipient: event.recipient,
            message: event.message,
            occurredAt: event.occurred_at,
        })),
    };
}

/**
 * Krijgen de verstuurde rapporten van het bedrijf hun PDF? Voor Instellingen:
 * zegt de PDF-knop "de PDF-dienst op de server is niet ingesteld", dan staat
 * het daar ook, met hoeveel rapporten er wachten.
 */
export interface PdfBacklog {
    /** Verstuurde rapporten zonder PDF na een mislukte poging. */
    waiting: number;
    /** De PDF-dienst op de server ontbreekt; opnieuw proberen helpt niet. */
    setupRequired: boolean;
    lastFailureAt: string | null;
}

export function toPdfBacklog(resource: PdfBacklogResource): PdfBacklog {
    return {
        waiting: resource.waiting,
        setupRequired: resource.setup_required,
        lastFailureAt: resource.last_failure_at,
    };
}
